import { memo, useMemo, useRef } from 'react';
import addMinutes from 'date-fns/addMinutes';
import format from 'date-fns/format';

import useEvent from '@lyearn/core/hooks/useEvent';
import { useTranslation } from '@lyearn/i18n';
import { AddBlock } from '@lyearn/molecules';
import { Stack } from '@lyearn/ui';
import { convertTo12HourFormat } from '@/components/Form/components/Frequency/utils';

import HabitReminderInput from './components/HabitReminderInput';

interface Props {
  value: string[];
  onChange: (value: string[]) => void;
  error?: string[];
}

const MAX_ALLOWED_REMINDERS = 10;

const REMINDER_OPTIONS = [
  { value: '06:00', label: '6:00 AM' },
  { value: '09:00', label: '9:00 AM' },
  { value: '12:00', label: '12:00 PM' },
  { value: '15:00', label: '3:00 PM' },
  { value: '18:00', label: '6:00 PM' },
  { value: '21:00', label: '9:00 PM' },
];

const HabitReminders = (props: Props) => {
  const { t } = useTranslation();
  const { value = [], onChange, error } = props;
  const canAddReminder = value.length < MAX_ALLOWED_REMINDERS;
  const autoFocusIndexRef = useRef<number | null>(null);

  const availabledOptions = useMemo(
    () =>
      value.length
        ? REMINDER_OPTIONS.filter((option) => !value.includes(option.value))
        : REMINDER_OPTIONS,
    [value],
  );

  const onAddReminder = useEvent(() => {
    const now = new Date();

    if (!value.length) {
      onChange([...value, format(now, 'HH:mm')]);
    } else {
      const [hour, min] = value[value.length - 1].split(':');
      const dateAtLastReminder = now.setHours(Number(hour), Number(min));
      onChange([...value, format(addMinutes(dateAtLastReminder, 30), 'HH:mm')]);
    }
    autoFocusIndexRef.current = value.length;
  });

  const onChangeReminder = useEvent((indexToUpdate: number, nextReminder: string) => {
    onChange(value.map((reminder, index) => (index === indexToUpdate ? nextReminder : reminder)));
    autoFocusIndexRef.current = null;
  });

  const onDeleteReminder = useEvent((indexToDelete: number) => {
    onChange(value.filter((_, index) => index !== indexToDelete));
    autoFocusIndexRef.current = null;
  });

  return (
    <Stack direction="col" spacing="gap-8">
      {value.map((reminder, index) => (
        <HabitReminderInput
          key={index}
          autoFocus={index === autoFocusIndexRef.current}
          error={
            error?.includes(reminder)
              ? t('Habits:Editor.ReminderErrorMessage', {
                  value: convertTo12HourFormat(reminder),
                })
              : undefined
          }
          index={index}
          options={availabledOptions}
          value={reminder}
          onChange={onChangeReminder}
          onDelete={onDeleteReminder}
        />
      ))}
      {canAddReminder ? (
        <AddBlock length="long" title={t('Habits:Editor.AddTime')} onClick={onAddReminder} />
      ) : null}
    </Stack>
  );
};

export default memo(HabitReminders);
